import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import './BottomFeedback.css'

export default function BottomFeedback() {
  const [notice, setNotice] = useState(null)
  const [confirmation, setConfirmation] = useState(null)
  const pending = useRef(null)
  const returnFocus = useRef(null)
  const cancelButton = useRef(null)

  const finish = value => {
    pending.current?.resolve(value)
    pending.current = null
    setConfirmation(null)
    if (returnFocus.current?.isConnected) returnFocus.current.focus()
  }

  useEffect(() => {
    const feedback = event => setNotice(event.detail)
    const invalid = event => {
      event.preventDefault()
      const controls = event.target.form ? Array.from(event.target.form.elements) : [event.target]
      const names = controls.filter(control => control.willValidate && !control.validity.valid)
        .map(control => control.labels?.[0]?.textContent?.trim() || control.name?.replaceAll('_', ' ') || control.placeholder || 'required field')
      setNotice({ message: `Please check: ${[...new Set(names)].join(', ')}.`, kind: 'error' })
    }
    const confirm = event => {
      pending.current?.resolve(false)
      pending.current = event.detail
      returnFocus.current = document.activeElement
      setNotice(null)
      setConfirmation(event.detail)
    }
    const escape = event => {
      if (event.key === 'Escape' && pending.current) {
        pending.current.resolve(false)
        pending.current = null
        setConfirmation(null)
        if (returnFocus.current?.isConnected) returnFocus.current.focus()
      }
    }
    window.addEventListener('app-feedback', feedback)
    window.addEventListener('app-confirm', confirm)
    window.addEventListener('keydown', escape)
    document.addEventListener('invalid', invalid, true)
    return () => {
      window.removeEventListener('app-feedback', feedback)
      window.removeEventListener('app-confirm', confirm)
      window.removeEventListener('keydown', escape)
      document.removeEventListener('invalid', invalid, true)
      pending.current?.resolve(false)
    }
  }, [])

  useEffect(() => { if (confirmation) cancelButton.current?.focus() }, [confirmation])

  return createPortal(<>
    {notice && <div className={`bottom-feedback bottom-feedback-${notice.kind}`} role={notice.kind === 'error' ? 'alert' : 'status'}>
      <span>{notice.message}</span>
      <button type="button" aria-label="Dismiss notification" onClick={() => setNotice(null)}>×</button>
    </div>}
    {confirmation && <div className="bottom-confirm-backdrop" onClick={event => { if (event.target === event.currentTarget) finish(false) }}>
      <section className="bottom-confirm" role="alertdialog" aria-modal="true" aria-labelledby="bottom-confirm-title" aria-describedby="bottom-confirm-message"
        onKeyDown={event => {
          if (event.key === 'Tab') {
            const buttons = event.currentTarget.querySelectorAll('button')
            if (event.shiftKey && document.activeElement === buttons[0]) { event.preventDefault(); buttons[buttons.length - 1].focus() }
            else if (!event.shiftKey && document.activeElement === buttons[buttons.length - 1]) { event.preventDefault(); buttons[0].focus() }
          }
        }}>
        <h2 id="bottom-confirm-title">Confirm action</h2>
        <p id="bottom-confirm-message">{confirmation.message}</p>
        <div className="bottom-confirm-actions">
          <button type="button" ref={cancelButton} onClick={() => finish(false)}>Cancel</button>
          <button type="button" onClick={() => finish(true)}>Confirm</button>
        </div>
      </section>
    </div>}
  </>, document.body)
}
